import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SiteInfoCard from './components/SiteInfoCard';
import DocumentsTable from './components/DocumentsTable';
import WorkersTable from './components/WorkersTable';
import { siteService } from '../../services/siteService';
import { auditService } from '../../services/auditService';
import { drawingService } from '../../services/drawingService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';


const SiteDashboard = () => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const highlightSection = searchParams?.get('highlight');
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('documents');
  const [loading, setLoading] = useState(false);
  const [siteData, setSiteData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const { userProfile } = useAuth();

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Sites', href: '/admin-dashboard' },
    { label: siteData?.site_name || 'Site Dashboard' }
  ];

  useEffect(() => {
    // Set active tab based on highlight parameter
    if (highlightSection === 'workers') {
      setActiveTab('workers');
    } else if (highlightSection === 'documents') {
      setActiveTab('documents');
    }
  }, [highlightSection]);

  useEffect(() => {
    if (siteId) {
      loadSiteData(siteId);
    } else {
      // Safe fallback: redirect to admin dashboard if no siteId
      navigate('/admin-dashboard', { replace: true });
    }
  }, [siteId, navigate]);

  const loadSiteData = async (id) => {
    try {
      setLoading(true);
      const siteResult = await siteService?.getSiteById(id);
      if (siteResult?.data) {
        setSiteData(siteResult?.data);
        
        // Load audits and drawings for this site
        const [auditsResult, drawingsResult] = await Promise.all([
          auditService?.getAuditsBySite(id),
          drawingService?.getLatestDrawings(id)
        ]);
        
        if (auditsResult?.data) setAudits(auditsResult?.data);
        if (drawingsResult?.data) setDrawings(drawingsResult?.data);
      } else {
        // Site not found, redirect to admin dashboard
        navigate('/admin-dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error loading site data:', err);
      // On error, redirect to admin dashboard
      navigate('/admin-dashboard', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentView = (id) => {
    console.log('View document:', id);
  };

  const handleDocumentEdit = (id) => {
    console.log('Edit document:', id);
  };

  const handleDocumentDelete = (id) => {
    console.log('Delete document:', id);
  };

  const handleViewInduction = (id) => {
    console.log('View induction:', id);
  };

  const handleEditWorker = (id) => {
    console.log('Edit worker:', id);
  };

  const handleExportPDF = () => {
    console.log('Exporting site compliance report as PDF');
  };

  const handleUploadDocument = () => {
    navigate('/document-management');
  };

  const handleAssignWorker = () => {
    console.log('Assign worker to site');
  };

  const tabs = [
    { id: 'documents', label: t('documents'), icon: 'FileText', count: documents?.length },
    { id: 'workers', label: t('workers'), icon: 'Users', count: workers?.length },
    { id: 'incidents', label: t('incidents'), icon: 'AlertTriangle', count: 0 },
    { id: 'checklists', label: t('checklists'), icon: 'CheckSquare', count: 0 },
    { id: 'audits', label: t('audits'), icon: 'ClipboardCheck', count: audits?.length },
    { id: 'drawings', label: t('drawings'), icon: 'FileImage', count: drawings?.length }
  ];

  return (
    <>
      <Helmet>
        <title>{siteData?.site_name || 'Site Dashboard'} - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          ) : (
            <>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    {siteData?.site_name || 'Site Dashboard'}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {siteData?.address_line1 && siteData?.city ? `${siteData?.address_line1}, ${siteData?.city}` : 'Site details'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    iconName="FileText"
                    onClick={() => navigate('/compliance-report')}
                    title={t('viewComplianceReport')}
                  >
                    {t('complianceReport')}
                  </Button>
                  <Button
                    variant="outline"
                    iconName="Upload"
                    onClick={() => navigate('/document-management')}
                    title={t('uploadDocument')}
                  >
                    {t('uploadDocument')}
                  </Button>
                </div>
              </div>

              <SiteInfoCard siteData={siteData} />

              <div className="bg-card border border-border rounded-lg shadow-elevation-2">
                <div className="border-b border-border">
                  <div className="flex items-center overflow-x-auto">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium transition-smooth whitespace-nowrap flex-shrink-0 ${
                          activeTab === tab?.id
                            ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon name={tab?.icon} size={20} />
                        <span>{tab?.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          activeTab === tab?.id
                            ? 'bg-accent text-accent-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {tab?.count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  {activeTab === 'documents' && (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-lg font-semibold text-foreground">{t('siteDocuments')}</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleUploadDocument}
                          iconName="Plus"
                          iconPosition="left"
                        >
                          {t('addDocument')}
                        </Button>
                      </div>
                      <DocumentsTable
                        documents={documents}
                        onView={handleDocumentView}
                        onEdit={handleDocumentEdit}
                        onDelete={handleDocumentDelete}
                      />
                    </div>
                  )}

                  {activeTab === 'workers' && (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-lg font-semibold text-foreground">{t('assignedWorkers')}</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleAssignWorker}
                          iconName="UserPlus"
                          iconPosition="left"
                        >
                          {t('assignWorker')}
                        </Button>
                      </div>
                      <WorkersTable
                        workers={workers}
                        onViewInduction={handleViewInduction}
                        onEditWorker={handleEditWorker}
                      />
                    </div>
                  )}

                  {activeTab === 'incidents' && (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-lg font-semibold text-foreground">{t('siteIncidents')}</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/incident-reporting?siteId=${siteId}`)}
                          iconName="Plus"
                          iconPosition="left"
                        >
                          {t('reportIncident')}
                        </Button>
                      </div>
                      <div className="text-center py-12">
                        <Icon name="AlertTriangle" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h4 className="text-lg font-semibold text-foreground mb-2">{t('noIncidentsYet')}</h4>
                        <p className="text-sm text-muted-foreground mb-6">
                          {t('noIncidentsYetDesc')}
                        </p>
                        <Button
                          variant="default"
                          onClick={() => navigate(`/incident-reporting?siteId=${siteId}`)}
                          iconName="Plus"
                        >
                          {t('reportFirstIncident')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'checklists' && (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-lg font-semibold text-foreground">{t('dailyChecklists')}</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/daily-site-checklist?siteId=${siteId}`)}
                          iconName="Plus"
                          iconPosition="left"
                        >
                          {t('completeChecklist')}
                        </Button>
                      </div>
                      <div className="text-center py-12">
                        <Icon name="CheckSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h4 className="text-lg font-semibold text-foreground mb-2">{t('noChecklistsYet')}</h4>
                        <p className="text-sm text-muted-foreground mb-6">
                          {t('noChecklistsYetDesc')}
                        </p>
                        <Button
                          variant="default"
                          onClick={() => navigate(`/daily-site-checklist?siteId=${siteId}`)}
                          iconName="CheckSquare"
                        >
                          {t('completeFirstChecklist')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'audits' && (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-lg font-semibold text-foreground">{t('siteAuditsInspections')}</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/audits-inspections?siteId=${siteId}`)}
                          iconName="Plus"
                          iconPosition="left"
                        >
                          {t('createAudit')}
                        </Button>
                      </div>
                      {audits?.length > 0 ? (
                        <div className="grid gap-4">
                          {audits?.map((audit) => (
                            <div
                              key={audit?.id}
                              className="p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => navigate(`/audits-inspections?siteId=${siteId}`)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-foreground">{audit?.audit_name}</h4>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  audit?.status === 'completed' ? 'bg-success/10 text-success' :
                                  audit?.status === 'in_progress'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                                }`}>
                                  {audit?.status?.replace('_', ' ')?.toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {new Date(audit?.created_at)?.toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="ClipboardCheck" size={48} className="text-muted-foreground mx-auto mb-4" />
                          <h4 className="text-lg font-semibold text-foreground mb-2">{t('noAuditsYet')}</h4>
                          <p className="text-sm text-muted-foreground mb-6">
                            {t('noAuditsYetDesc')}
                          </p>
                          <Button
                            variant="default"
                            onClick={() => navigate(`/audits-inspections?siteId=${siteId}`)}
                            iconName="Plus"
                          >
                            {t('createFirstAudit')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'drawings' && (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h3 className="text-lg font-semibold text-foreground">{t('drawingsPlans')}</h3>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => navigate(`/drawings-plans?siteId=${siteId}`)}
                          iconName="Upload"
                          iconPosition="left"
                        >
                          {t('uploadDrawing')}
                        </Button>
                      </div>
                      {drawings?.length > 0 ? (
                        <div className="grid gap-4">
                          {drawings?.map((drawing) => (
                            <div
                              key={drawing?.id}
                              className="p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => navigate(`/drawings-plans?siteId=${siteId}`)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="font-semibold text-foreground">{drawing?.drawing_name}</h4>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {t('version')} {drawing?.version_number} • {drawing?.drawing_number}
                                  </p>
                                </div>
                                <Icon name="FileImage" size={20} className="text-accent" />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {t('uploaded')} {new Date(drawing?.created_at)?.toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="FileImage" size={48} className="text-muted-foreground mx-auto mb-4" />
                          <h4 className="text-lg font-semibold text-foreground mb-2">{t('noDrawingsYet')}</h4>
                          <p className="text-sm text-muted-foreground mb-6">
                            {t('noDrawingsYetDesc')}
                          </p>
                          <Button
                            variant="default"
                            onClick={() => navigate(`/drawings-plans?siteId=${siteId}`)}
                            iconName="Upload"
                          >
                            {t('uploadFirstDrawing')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default SiteDashboard;